import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import {
  AlarmOutlined as AlarmIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { formatDate } from "../../../utils/formatters";
import { colors } from "../../../styles/colors";
import { Urgency } from "../../../utils/urgency";

interface DeadlineCellProps {
  value?: string;
  urgency: Urgency;
  /**
   * Welches Datum hier steht ("Lieferung bis", "Geliefert am", …). Steht vor dem Datum, weil
   * es die Zeile erst lesbar macht: Ohne die Benennung ist ein Datum in einer Spalte, die je
   * nach Status ein anderes zeigt, nicht einzuordnen.
   *
   * Fachlich aufgelöst wird das anderswo – hier kommt der fertige Text an.
   */
  label?: string;
}

/**
 * Das Dringlichkeits-Icon steht hinter dem Datum, nicht davor: So beginnen Benennung und Datum
 * bündig mit dem Zellrand und mit einer Untertitelzeile darunter, und Zeilen mit und ohne Icon
 * fluchten ohne Platzhalter.
 */
const ICON_GROESSE = "1.1rem";

/**
 * Die Icons der Dringlichkeit – überall dieselben: in Termin-Tabellen, am Kalenderblatt der
 * Startseite und als Problemsignal in der Projektübersicht. Wecker heißt überfällig, Uhr heißt
 * in den nächsten 14 Tagen fällig.
 */
export const URGENCY_ICON = {
  overdue: AlarmIcon,
  soon: AccessTimeIcon,
} as const;

export const URGENCY_COLOR = {
  overdue: colors.status.error.main,
  soon: colors.status.warning.accent,
} as const;

export const URGENCY_LABEL = {
  overdue: "Überfällig",
  soon: "Fällig in den nächsten 14 Tagen",
} as const;

/**
 * Dringlichkeit als Icon – überfällig rot, in den nächsten 14 Tagen eine Uhr, sonst nichts. Die
 * einzige Kennzeichnung: Zeilen werden nicht eingefärbt. In Termin-Tabellen steht es direkt
 * hinter der Bezeichnung, in anderen Tabellen hinter dem Datum (`DeadlineCell`).
 */
export const UrgencyIcon: React.FC<{ urgency: Urgency }> = ({ urgency }) => (
  <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
    {urgency === "overdue" && (
      <Tooltip title={URGENCY_LABEL.overdue} arrow>
        <AlarmIcon
          sx={{ color: URGENCY_COLOR.overdue, fontSize: ICON_GROESSE }}
        />
      </Tooltip>
    )}
    {urgency === "soon" && (
      <Tooltip title={URGENCY_LABEL.soon} arrow>
        <AccessTimeIcon
          sx={{ color: URGENCY_COLOR.soon, fontSize: ICON_GROESSE }}
        />
      </Tooltip>
    )}
  </Box>
);

const DeadlineCell: React.FC<DeadlineCellProps> = ({
  value,
  urgency,
  label,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
    {label && (
      <Typography
        variant="caption"
        noWrap
        sx={{ color: colors.text.secondary }}
      >
        {label}
      </Typography>
    )}
    <Typography variant="body2" noWrap>
      {value ? formatDate(value) : "–"}
    </Typography>
    <UrgencyIcon urgency={urgency} />
  </Box>
);

export default DeadlineCell;
